import moment, { Moment } from 'moment';
import { ActivityPeriod, ActivityPeriodUnit } from '../type';

export type ActivityPeriodMode = 'all' | ActivityPeriod['mode'];

/** What the merchant is typing, complete or not; only a complete one becomes a filter. */
export interface ActivityPeriodDraft {
  mode: ActivityPeriodMode;
  from: Moment | null;
  to: Moment | null;
  count: string;
  unit: ActivityPeriodUnit;
}

export type ActivityRangeError = 'order' | 'future' | 'invalid';

const DAY_FORMAT = 'YYYY-MM-DD';
// The API's own bound on activityLast.
export const MAX_ACTIVITY_COUNT = 3650;

export const EMPTY_ACTIVITY_DRAFT: ActivityPeriodDraft = {
  mode: 'all',
  from: null,
  to: null,
  count: '',
  unit: 'DAYS',
};

export const draftFromPeriod = (period?: ActivityPeriod): ActivityPeriodDraft => {
  if (!period) return EMPTY_ACTIVITY_DRAFT;
  if (period.mode === 'range') {
    return {
      ...EMPTY_ACTIVITY_DRAFT,
      mode: 'range',
      from: moment(period.from, DAY_FORMAT, true),
      to: moment(period.to, DAY_FORMAT, true),
    };
  }
  return { ...EMPTY_ACTIVITY_DRAFT, mode: 'last', count: String(period.count), unit: period.unit };
};

export const activityRangeError = (draft: ActivityPeriodDraft, today: Moment): ActivityRangeError | null => {
  const { from, to } = draft;
  if ((from && !from.isValid()) || (to && !to.isValid())) return 'invalid';
  if ((from && from.isAfter(today, 'day')) || (to && to.isAfter(today, 'day'))) return 'future';
  if (from && to && from.isAfter(to, 'day')) return 'order';
  return null;
};

const parseCount = (count: string): number | undefined => {
  if (!/^\d+$/.test(count)) return undefined;
  const value = Number(count);
  return value >= 1 && value <= MAX_ACTIVITY_COUNT ? value : undefined;
};

export const periodFromDraft = (draft: ActivityPeriodDraft, today: Moment): ActivityPeriod | undefined => {
  if (draft.mode === 'last') {
    const count = parseCount(draft.count);
    return count ? { mode: 'last', count, unit: draft.unit } : undefined;
  }
  if (draft.mode === 'range' && draft.from && draft.to && !activityRangeError(draft, today)) {
    return { mode: 'range', from: draft.from.format(DAY_FORMAT), to: draft.to.format(DAY_FORMAT) };
  }
  return undefined;
};

export const isSamePeriod = (a?: ActivityPeriod, b?: ActivityPeriod): boolean => {
  if (!a || !b) return a === b;
  if (a.mode === 'range' && b.mode === 'range') return a.from === b.from && a.to === b.to;
  if (a.mode === 'last' && b.mode === 'last') return a.count === b.count && a.unit === b.unit;
  return false;
};

export const ACTIVITY_SHORTCUTS: ActivityPeriod[] = [
  { mode: 'last', count: 7, unit: 'DAYS' },
  { mode: 'last', count: 30, unit: 'DAYS' },
  { mode: 'last', count: 3, unit: 'MONTHS' },
  { mode: 'last', count: 12, unit: 'MONTHS' },
];

export const shortcutIndexOf = (period?: ActivityPeriod): number =>
  ACTIVITY_SHORTCUTS.findIndex(shortcut => isSamePeriod(shortcut, period));

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const activityPeriodLabel = (period: ActivityPeriod | undefined, t: Translate): string => {
  const key = 'pages.clients.activityPeriod.summary';
  if (!period) return t('pages.clients.activityPeriod.mode.all');
  if (period.mode === 'last') {
    if (period.count === 1) return t(period.unit === 'DAYS' ? `${key}.today` : `${key}.lastMonth`);
    return t(period.unit === 'DAYS' ? `${key}.lastDays` : `${key}.lastMonths`, { count: period.count });
  }
  const dateFormat = t('basic.date.format2');
  const from = moment(period.from, DAY_FORMAT, true).format(dateFormat);
  const to = moment(period.to, DAY_FORMAT, true).format(dateFormat);
  return period.from === period.to ? t(`${key}.day`, { date: from }) : t(`${key}.range`, { from, to });
};
